import { Helmet } from 'react-helmet-async';
import seoConfig from '@/config/seo.json';
import { translatePathToEn } from '@/config/pathMap';

// Type is inferred from the JSON pages object
type PageKey = keyof typeof seoConfig.pages;

interface UseSEOOptions {
  page: PageKey;
  locale?: 'de' | 'en';
}

/**
 * Returns a Helmet element with all SEO meta tags for the given page.
 * Use as: <SEOHead page="home" locale="de" />
 */
export const SEOHead = ({ page, locale = 'de' }: UseSEOOptions) => {
  const pageData = seoConfig.pages[page];
  if (!pageData) return null;

  const localeData = pageData[locale];
  if (!localeData) return null;

  const basePath = pageData.path; // German base path, e.g. "/loesungen/marketing"
  const enPath = basePath === '/' ? '/en' : `/en${translatePathToEn(basePath)}`;
  const dePath = basePath;

  // Canonical must point to the current locale's URL
  const canonicalUrl = locale === 'en'
    ? `${seoConfig.site.url}${enPath}`
    : `${seoConfig.site.url}${dePath}`;

  const ogLocale = locale === 'de' ? 'de_DE' : 'en_US';

  return (
    <Helmet>
      <title>{localeData.title}</title>
      <meta name="description" content={localeData.description} />
      <meta name="keywords" content={localeData.keywords} />

      {/* Open Graph */}
      <meta property="og:title" content={localeData.ogTitle} />
      <meta property="og:description" content={localeData.ogDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={seoConfig.site.name} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={localeData.ogTitle} />
      <meta name="twitter:description" content={localeData.ogDescription} />

      {/* Canonical — must match the current page's locale URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang alternates with correctly translated paths */}
      <link rel="alternate" hrefLang="de" href={`${seoConfig.site.url}${dePath}`} />
      <link rel="alternate" hrefLang="en" href={`${seoConfig.site.url}${enPath}`} />
      <link rel="alternate" hrefLang="x-default" href={`${seoConfig.site.url}${dePath}`} />
    </Helmet>
  );
};

/**
 * Legacy hook – still works but prefer <SEOHead /> for SSG compatibility.
 */
export const useSEO = ({ page, locale = 'de' }: UseSEOOptions) => {
  // No-op: the SEOHead component handles everything declaratively now.
  // Kept for backward compatibility – pages that still call useSEO() won't break.
};

export const getStructuredData = (type: 'organization' | 'software') => {
  return JSON.stringify(seoConfig.structuredData[type]);
};

export default useSEO;
